import StepButtons from './StepButtons'
import * as React from 'react';
import { useContext, useState } from 'react';
import { Label, Input, Row, Col, Card, CardBody, CardTitle, Button, InputGroupAddon, InputGroup, InputGroupText } from 'reactstrap';
import Select from 'react-select';
import { TeamsRequest, IDraftRequest, DraftClient, CreateDraftCommand, DraftRequest } from '../../../WorldDoomLeague';
import { ErrorContext } from '../../../state';
import { StepWizardChildProps } from 'react-step-wizard';
import { ICaptainList, IPlayerList } from './NewSeasonWizardInterfaces';

interface RegisterDraftProps extends StepWizardChildProps {
    captains: ICaptainList[],
    setCaptains: React.Dispatch<React.SetStateAction<ICaptainList[]>>,
    players: IPlayerList[],
    setPlayers: React.Dispatch<React.SetStateAction<IPlayerList[]>>,
    season: number
};

const RegisterDraft = (props: RegisterDraftProps) => {
    const error = useContext(ErrorContext);
    const [draftList, setDraftList] = useState<IDraftRequest[]>([{
        nominatedPlayer: null,
        nominatingPlayer: null,
        playerSoldTo: null,
        sellPrice: 0
    }]);
    const [playersPerTeam, setPlayersPerTeam] = useState(3);
    const [canSubmitDraft, setCanSubmitDraft] = useState(false);
    const [completedDraft, setCompletedDraft] = useState(false);
    const [canCreateDraft, setCanCreateDraft] = useState(true);

    const createDraftPicks = () => {
        setCanCreateDraft(false);

        const pad_array = (arr, len, fill) => {
            return arr.concat(Array(len).fill(fill)).slice(0, len);
        };

        if (props.captains.length > 0) {
            const maxPlayersDrafted = props.captains.length * playersPerTeam;

            const newDraftList = pad_array(draftList, maxPlayersDrafted, {
                nominatedPlayer: null,
                nominatingPlayer: null,
                playerSoldTo: null,
                sellPrice: 0
            });

            const playersPerTeamMinusCaptain = playersPerTeam;

            const newCaptainArray = props.captains.map((s, _idx) => {
                return { ...s, playersLeft: playersPerTeamMinusCaptain };
            });

            props.setCaptains(newCaptainArray);

            setDraftList(newDraftList);
        }
    };

    const handleNominatingSelected = (idx: number, playerId: number) => {
        const newNominatingPlayer = draftList.map((draft, sidx) => {
            if (idx !== sidx) return draft;
            return { ...draft, nominatingPlayer: playerId };
        });

        setDraftList(newNominatingPlayer);
        checkIfFormComplete(newNominatingPlayer);
    };

    // Handle the sell price
    const handleSoldForInput = (idx: number, value: number) => {
        const newSellPrice = draftList.map((draft, sidx) => {
            if (idx !== sidx) return draft;
            return { ...draft, sellPrice: value };
        });

        setDraftList(newSellPrice);
        checkIfFormComplete(newSellPrice);
    };

    const handleSoldToSelected = (idx: number, playerId: number) => {
        // subtract the playersleft to handle disables
        const newCaptainsArray = props.captains.map((s, _idx) => {
            if (s.id !== playerId) return s;
            const left = s.playersLeft - 1;
            return { ...s, playersLeft: left};
        });

        // re-enable a captain if they are deselected.
        if (draftList[idx].playerSoldTo !== null) {
            const reenabledOldCaptain = newCaptainsArray.map((s, _idx) => {
                if (s.id !== draftList[idx].playerSoldTo) return s;
                const left = s.playersLeft + 1;
                return { ...s, playersLeft: left };
            });
            const newCaptainList = reenabledOldCaptain.map((s, _idx) => {
                if (s.playersLeft > 0) return { ...s, isdisabled: false };
                return { ...s, isdisabled: true };
            });
            props.setCaptains(newCaptainList);
        } else {
            const newCaptainList = newCaptainsArray.map((s, _idx) => {
                if (s.playersLeft > 0) return { ...s, isdisabled: false };
                return { ...s, isdisabled: true };
            });
            props.setCaptains(newCaptainList);
        }

        const newCaptainSoldTo = draftList.map((draft, sidx) => {
            if (idx !== sidx) return draft;
            return { ...draft, playerSoldTo: playerId };
        });

        setDraftList(newCaptainSoldTo);
        checkIfFormComplete(newCaptainSoldTo);
    };

    const handleNominatedSelected = (idx: number, playerId: number) => {
        const newPlayerArray = props.players.map((s, _idx) => {
            if (s.id !== playerId) return s;
            return { ...s, isdisabled: true };
        });

        if (draftList[idx].nominatedPlayer !== null) {
            const reenabledOldPlayer = newPlayerArray.map((s, _idx) => {
                if (s.id !== draftList[idx].nominatedPlayer) return s;
                return { ...s, isdisabled: false };
            });
            props.setPlayers(reenabledOldPlayer);
        } else {
            props.setPlayers(newPlayerArray);
        }

        const newCaptain = draftList.map((draft, sidx) => {
            if (idx !== sidx) return draft;
            return { ...draft, nominatedPlayer: playerId };
        });

        setDraftList(newCaptain);
        checkIfFormComplete(newCaptain);
    };

    const createDraft = async (evt) => {
        try {
            let client = new DraftClient();
            const command = new CreateDraftCommand;
            // Need to do this to get around an NSwag generator bug.
            // https://github.com/RicoSuter/NSwag/issues/2862
            let draft: DraftRequest[] = [];
            for (var idx = 0; idx < draftList.length; idx++) {
                var addPick = new DraftRequest();
                addPick.nominatedPlayer = draftList[idx].nominatedPlayer;
                addPick.nominatingPlayer = draftList[idx].nominatingPlayer;
                addPick.playerSoldTo = draftList[idx].playerSoldTo;
                addPick.sellPrice = draftList[idx].sellPrice;
                draft.push(addPick);
            }

            command.season = props.season;
            command.draftRequestList = draft;
            const response = await client.create(command);
            setCanSubmitDraft(false);
            setCompletedDraft(true);
        } catch (e) {
            error.setError(JSON.parse(e.response));
        }
    };

    const checkIfFormComplete = (draft: IDraftRequest[]) => {
        if (draft.every(element => element.nominatedPlayer && element.nominatingPlayer && element.playerSoldTo && element.sellPrice > 0)) {
            setCanSubmitDraft(true);
        } else {
            setCanSubmitDraft(false);
        }
    };

    // create a list for each captain who bought a player.
    const renderSoldForInput = (idx) => {
        let select = null;
            select = (
                <InputGroup>
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText>$</InputGroupText>
                    </InputGroupAddon>
                    <Input placeholder="Amount" min={1} max={28} type="number" step="1" disabled={completedDraft} onChange={e => handleSoldForInput(idx, parseInt(e.target.value))} />
                </InputGroup>)

        return (select);
    };

    // create a list for each captain who bought a player.
    const renderSoldToDropdown = (idx) => {
        let select = null;
        if (props.captains.length > 0) {
            select = (
                <Select
                    options={props.captains}
                    onChange={e => handleSoldToSelected(idx, e.id)}
                    value={props.captains.find(o => o.id == draftList[idx].playerSoldTo) || null}
                    getOptionValue={value => value.id.toString()}
                    getOptionLabel={label => label.playerName}
                    isOptionDisabled={(option) => option.isdisabled}
                    isDisabled={completedDraft}
                    isSearchable={true}
                    theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'dimgrey',
                            neutral0: 'black',
                            neutral80: 'white'
                        },
                    })}
                />)
        } else {
            select = (<Select options={[{ label: "No captains left!", value: "Not" }]} isDisabled={completedDraft} theme={theme => ({
                ...theme,
                borderRadius: 0,
                colors: {
                    ...theme.colors,
                    primary25: 'dimgrey',
                    neutral0: 'black',
                    neutral80: 'white'
                },
            })} />);
        }

        return (select);
    };

    // create a list for each nominating captain.
    const renderNominatingCaptainDropdown = (idx: number) => {
        let select = null;
        if (props.captains.length > 0) {
            select = (
                <Select
                    options={props.captains}
                    onChange={e => handleNominatingSelected(idx, e.id)}
                    value={props.captains.find(o => o.id == draftList[idx].nominatingPlayer) || null}
                    getOptionValue={value => value.id.toString()}
                    getOptionLabel={label => label.playerName}
                    isOptionDisabled={(option) => option.isdisabled}
                    isDisabled={completedDraft}
                    isSearchable={true}
                    theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'dimgrey',
                            neutral0: 'black',
                            neutral80: 'white'
                        },
                    })}
                />)
        } else {
            select = (<Select options={[{ label: "No captains left!", value: "Not" }]} isDisabled={completedDraft} theme={theme => ({
                ...theme,
                borderRadius: 0,
                colors: {
                    ...theme.colors,
                    primary25: 'dimgrey',
                    neutral0: 'black',
                    neutral80: 'white'
                },
            })} />);
        }

        return (select);
    };

    // create a list for each nominated player.
    const renderNominatedPlayerDropdown = (idx: number) => {
        let select = null;
        if (props.players.length > 0) {
            select = (
                <Select
                    options={props.players}
                    onChange={e => handleNominatedSelected(idx, e.id)}
                    value={props.players.find(o => o.id == draftList[idx].nominatedPlayer) || null}
                    getOptionValue={value => value.id.toString()}
                    getOptionLabel={label => label.playerName}
                    isOptionDisabled={(option) => option.isdisabled}
                    isDisabled={completedDraft}
                    isSearchable={true}
                    theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'dimgrey',
                            neutral0: 'black',
                            neutral80: 'white'
                        },
                    })}
                />)
        } else {
            select = (<Select options={[{ label: "No players left!", value: "Not" }]} isDisabled={completedDraft} theme={theme => ({
                ...theme,
                borderRadius: 0,
                colors: {
                    ...theme.colors,
                    primary25: 'dimgrey',
                    neutral0: 'black',
                    neutral80: 'white'
                },
            })} />);
        }

        return (select);
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <h3 className='text-center'>Register Draft</h3>
                    <p>Please input the draft information. When completed, the team rosters will be finalized.</p>
                    <Label for="amountTeams">Amount of players per team</Label>
                    <Input placeholder="Amount" name="amountPlayers" min={4} max={4} type="number" step="1" value={4} disabled={true} />
                    <br />
                    <Button color="primary" size="lg" block disabled={!canCreateDraft} onClick={createDraftPicks}>Create Draft Picks</Button>
                    <hr />
                </Col>
            </Row>
            <Row>
                {draftList && canCreateDraft == false && (draftList.map((draft, index) => (
                    <Col xs="6" sm="4">
                        <Card>
                            <CardBody>
                                <CardTitle tag="h5">Draft Pick #{index + 1}</CardTitle>
                                    Captain 
                                    {renderNominatingCaptainDropdown(index)}
                                    nominates player
                                    {renderNominatedPlayerDropdown(index)}
                                    who is bought by
                                    {renderSoldToDropdown(index)}
                                    for
                                    {renderSoldForInput(index)}
                            </CardBody>
                        </Card>
                        <br />
                    </Col>
                )))}
            </Row>
            <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <Button color="primary" size="lg" block disabled={!canSubmitDraft} onClick={createDraft}>Finalize Draft</Button>
                </Col>
            </Row>
        <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <StepButtons step={3} {...props} disabled={!completedDraft} />
            </Col>
        </Row>
        </React.Fragment>
    );
};

export default RegisterDraft;